"use client"

import { useState } from "react"
import { levels } from "@/data/levels"
import { heroTypes } from "@/data/hero-types"
import { setAudioSettings, useAudioSettings } from "@/lib/audio-settings"
import { setPlayerPrefs, usePlayerPrefs } from "@/lib/player-prefs"
import { resetPlayerStats, usePlayerStats } from "@/lib/player-stats"
import { resetCodexDiscovery, useCodexDiscovery } from "@/lib/codex-discovery"
import { useWallet } from "@/lib/wallet-provider"
import type { PersistedGameState } from "@/services/game-state-persistence"
import {
  MusicIcon,
  PlayIcon,
  TrashIcon,
  VolumeIcon,
  VolumeMutedIcon,
} from "@/components/icons/pixel-icons"
import {
  MenuActionRow,
  MenuDetailHeading,
  MenuEmptyState,
  MenuGroup,
  MenuNotice,
  MenuSegmentedRow,
  MenuStatRow,
  MenuToggleRow,
  MenuValueRow,
} from "../menu-primitives"

/** Volume slider + mute toggle, matching the pause menu's control. */
const VolumeRow = ({
  label,
  icon,
  mutedIcon,
  value,
  onChange,
}: {
  label: string
  icon: React.ReactNode
  mutedIcon: React.ReactNode
  value: number
  onChange: (next: number) => void
}) => {
  const isMuted = value === 0
  return (
    <div className="flex items-center gap-3 px-4 py-3 min-h-[52px]">
      <button
        type="button"
        onClick={() => onChange(isMuted ? 1 : 0)}
        className="shrink-0 text-amber-500/70 hover:text-amber-300 transition"
        aria-label={isMuted ? `Unmute ${label}` : `Mute ${label}`}
      >
        {isMuted ? mutedIcon : icon}
      </button>
      <span className="w-16 shrink-0 text-sm font-bold text-gray-100">{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={`${label} volume`}
        className="flex-1 accent-amber-500 cursor-pointer"
      />
      <span className="w-10 shrink-0 text-right text-xs text-gray-400 tabular-nums">
        {Math.round(value * 100)}%
      </span>
    </div>
  )
}

export const AudioDisplaySection = () => {
  const audio = useAudioSettings()
  const prefs = usePlayerPrefs()

  return (
    <div>
      <MenuDetailHeading
        title="Audio & display"
        blurb="Applies everywhere — the pause menu keeps quick mute toggles for mid-run adjustments."
      />

      <MenuGroup title="Audio" icon={<VolumeIcon size={14} />}>
        <VolumeRow
          label="Effects"
          icon={<VolumeIcon size={20} />}
          mutedIcon={<VolumeMutedIcon size={20} />}
          value={audio.sfx}
          onChange={(sfx) => setAudioSettings({ sfx })}
        />
        <VolumeRow
          label="Music"
          icon={<MusicIcon size={20} />}
          mutedIcon={<MusicIcon size={20} className="opacity-40" />}
          value={audio.music}
          onChange={(music) => setAudioSettings({ music })}
        />
      </MenuGroup>

      <MenuGroup title="Gameplay" icon={<PlayIcon size={14} />}>
        <MenuSegmentedRow
          label="Default speed"
          sublabel="Speed a new wave starts at"
          options={[
            { value: "1x", label: "1x" },
            { value: "2x", label: "2x" },
          ]}
          value={prefs.startFastForwarded ? "2x" : "1x"}
          onChange={(next) => setPlayerPrefs({ startFastForwarded: next === "2x" })}
        />
        <MenuToggleRow
          label="Pause while reading enemy cards"
          sublabel="Holds the wave whenever a new-enemy card is open, so reading costs no lives"
          checked={prefs.pauseOnEnemyCard}
          onChange={(pauseOnEnemyCard) => setPlayerPrefs({ pauseOnEnemyCard })}
        />
        <MenuToggleRow
          label="Reduce menu motion"
          sublabel="Skip the slide and fade transitions on this menu"
          checked={prefs.reducedMotion}
          onChange={(reducedMotion) => setPlayerPrefs({ reducedMotion })}
        />
      </MenuGroup>
    </div>
  )
}

export const HowToPlaySection = ({ onOpenTutorial }: { onOpenTutorial: () => void }) => (
  <div>
    <MenuDetailHeading
      title="How to play"
      blurb="Pelf is a tower defence roguelite: land, build towers along the path, level your hero, and survive every wave."
    />

    <MenuGroup title="Walkthrough">
      <MenuActionRow
        icon={<PlayIcon size={18} />}
        label="Open the tutorial"
        sublabel="Step-by-step walkthrough with screenshots"
        onClick={onOpenTutorial}
      />
    </MenuGroup>

    <MenuGroup title="The basics">
      <MenuValueRow label="Build" sublabel="Tap an empty grass tile, then pick a tower from the ring" />
      <MenuValueRow label="Upgrade" sublabel="Tap a tower you own to level it up or sell it back" />
      <MenuValueRow
        label="Your hero"
        sublabel="Tap the ground to move, tap an enemy to attack. Spend skill points as you level"
      />
      <MenuValueRow
        label="Waves"
        sublabel="Call the next wave early for bonus gold, or let it come on its own timer"
      />
      <MenuValueRow
        label="Between levels"
        sublabel="Sell loot at the shop, then choose your next stop on the star map"
      />
    </MenuGroup>
  </div>
)

export const RunSaveSection = ({
  pendingRun,
  onResumeRun,
  onDiscardRun,
}: {
  pendingRun?: PersistedGameState | null
  onResumeRun?: () => void
  onDiscardRun?: () => void
}) => {
  const stats = usePlayerStats()
  const discovery = useCodexDiscovery()
  const [confirmingReset, setConfirmingReset] = useState(false)
  const [resetDone, setResetDone] = useState<"stats" | "discovery" | null>(null)

  const discoveredCount =
    discovery.towers.length + discovery.enemies.length + discovery.items.length

  return (
    <div>
      <MenuDetailHeading
        title="Run & save data"
        blurb="Everything here lives in this browser. Clearing it does not touch the global ladder or anything published on-chain."
      />

      <MenuGroup title="Saved run">
        {pendingRun ? (
          <>
            <MenuValueRow
              label={levels[pendingRun.currentLevelIndex]?.name ?? "Unknown sector"}
              sublabel={`Wave ${pendingRun.currentWaveIndex + 1} · ${
                pendingRun.hero?.name ??
                heroTypes[pendingRun.selectedHeroType]?.name ??
                "Hero"
              } Lv ${pendingRun.hero?.level ?? 1} · ${pendingRun.stats.money}g`}
            />
            {onResumeRun && (
              <MenuActionRow
                icon={<PlayIcon size={18} />}
                label="Resume this run"
                sublabel="Pick up where you left off"
                onClick={onResumeRun}
              />
            )}
            {onDiscardRun && (
              <MenuActionRow
                icon={<TrashIcon size={18} />}
                label="Discard this run"
                sublabel="Delete the snapshot and start fresh next time"
                onClick={onDiscardRun}
                danger
              />
            )}
          </>
        ) : (
          <MenuEmptyState>No run is currently saved</MenuEmptyState>
        )}
      </MenuGroup>

      <MenuGroup title="Local records">
        <MenuStatRow label="Runs recorded" value={stats.runs.toLocaleString()} />
        <MenuStatRow label="Best score" value={stats.bestScore.toLocaleString()} />
        {confirmingReset ? (
          <div className="px-4 py-3">
            <p className="text-sm text-gray-300 mb-3">
              Erase every local run statistic on this device? This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  resetPlayerStats()
                  setConfirmingReset(false)
                  setResetDone("stats")
                }}
                className="flex-1 rounded-lg bg-game-danger hover:bg-game-danger-hover text-game-danger-foreground font-bold text-sm py-2 transition"
              >
                Erase records
              </button>
              <button
                type="button"
                onClick={() => setConfirmingReset(false)}
                className="flex-1 rounded-lg border border-amber-600/40 text-amber-200 font-bold text-sm py-2 hover:bg-amber-900/20 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <MenuActionRow
            icon={<TrashIcon size={18} />}
            label="Reset local records"
            sublabel="Clear run counts, best scores and per-hero stats"
            onClick={() => setConfirmingReset(true)}
            disabled={stats.runs === 0}
            danger
          />
        )}
        {resetDone === "stats" && (
          <div className="px-4 pb-3">
            <MenuNotice tone="success">Local records cleared</MenuNotice>
          </div>
        )}
      </MenuGroup>

      <MenuGroup title="Encyclopelfia progress">
        <MenuStatRow label="Entries discovered" value={discoveredCount.toLocaleString()} />
        <MenuActionRow
          icon={<TrashIcon size={18} />}
          label="Reset discoveries"
          sublabel="Re-locks every Encyclopelfia entry so you can uncover them again"
          onClick={() => {
            resetCodexDiscovery()
            setResetDone("discovery")
          }}
          disabled={discoveredCount === 0}
          danger
        />
        {resetDone === "discovery" && (
          <div className="px-4 pb-3">
            <MenuNotice tone="success">Encyclopelfia progress cleared</MenuNotice>
          </div>
        )}
      </MenuGroup>
    </div>
  )
}

export const AboutSection = () => {
  const { status, identityKey, network } = useWallet()
  const [copied, setCopied] = useState(false)

  const copyKey = async () => {
    if (!identityKey) return
    try {
      await navigator.clipboard.writeText(identityKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked - the key is still shown in full on hover */
    }
  }

  return (
    <div>
      <MenuDetailHeading title="About" blurb="Pelf — a tower defence roguelite on BSV." />

      <MenuGroup title="Connection">
        <MenuValueRow
          label="Wallet"
          sublabel={
            status === "connected"
              ? "BRC-100 wallet connected"
              : status === "connecting"
                ? "Connecting…"
                : "Not connected"
          }
        >
          <span className="text-xs font-bold text-amber-300 capitalize">{status}</span>
        </MenuValueRow>
        {identityKey && (
          <MenuValueRow label="Identity key" sublabel="Your public key on the ladder">
            <button
              type="button"
              onClick={copyKey}
              title={identityKey}
              className="text-xs font-mono text-gray-300 hover:text-amber-200 transition"
            >
              {copied ? "Copied" : `${identityKey.slice(0, 6)}…${identityKey.slice(-4)}`}
            </button>
          </MenuValueRow>
        )}
        {network && (
          <MenuValueRow label="Network" sublabel={network === "mainnet" ? "Live" : "Test network"}>
            <span className="text-xs font-bold text-gray-100 capitalize">{network}</span>
          </MenuValueRow>
        )}
      </MenuGroup>

      <MenuGroup title="Credits">
        <MenuValueRow label="Art" sublabel="Pixel tilesets, hero, tower, enemy and item sprite packs" />
        <MenuValueRow label="Audio" sublabel="Original score and effects" />
        <MenuValueRow label="Chain" sublabel="BSV via BRC-100 wallets and the @bsv/sdk" />
      </MenuGroup>

      <MenuGroup title="Content">
        <MenuStatRow label="Levels" value={levels.length} />
        <MenuStatRow label="Heroes" value={Object.keys(heroTypes).length} />
      </MenuGroup>
    </div>
  )
}
